// ============================================
// TABLE GRID COMPONENT
// Visual table layout with zones
// ============================================

import React from 'react';
import { Users, Clock, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';

const STATUS_CONFIG = {
  available: { 
    bg: 'bg-emerald-50', 
    border: 'border-emerald-300',
    hover: 'hover:border-emerald-500 hover:bg-emerald-100',
    dot: 'bg-emerald-500',
    text: 'text-emerald-700',
    label: 'Available' 
  },
  occupied: { 
    bg: 'bg-orange-50', 
    border: 'border-orange-300',
    hover: 'hover:border-orange-500 hover:bg-orange-100',
    dot: 'bg-orange-500',
    text: 'text-orange-700',
    label: 'Occupied' 
  },
  reserved: { 
    bg: 'bg-amber-50', 
    border: 'border-amber-300',
    hover: 'hover:border-amber-500 hover:bg-amber-100',
    dot: 'bg-amber-500',
    text: 'text-amber-700',
    label: 'Reserved' 
  },
  billing: { 
    bg: 'bg-red-50', 
    border: 'border-red-300',
    hover: 'hover:border-red-500 hover:bg-red-100',
    dot: 'bg-red-500',
    text: 'text-red-700',
    label: 'Billing' 
  },
};

const ZONE_CONFIG = {
  all: { label: 'All Tables', icon: '🏠' },
  main: { label: 'Main Floor', icon: '🪑' },
  window: { label: 'Window', icon: '🪟' },
  outdoor: { label: 'Outdoor', icon: '☀️' },
  bar: { label: 'Bar', icon: '🍸' },
};

// Format elapsed time
function formatElapsedTime(startTime) {
  if (!startTime) return '';
  const elapsed = Math.floor((Date.now() - new Date(startTime).getTime()) / 60000);
  if (elapsed < 60) return `${elapsed}m`;
  const hours = Math.floor(elapsed / 60);
  const mins = elapsed % 60;
  return `${hours}h ${mins}m`;
}

export function TableGrid({
  tables,
  selectedTable,
  onTableSelect,
  zones,
  selectedZone,
  onZoneChange,
  formatPrice,
}) {
  // Group tables by zone for display
  const tablesByZone = tables.reduce((acc, table) => {
    const zone = table.zone || 'main';
    if (!acc[zone]) acc[zone] = [];
    acc[zone].push(table);
    return acc;
  }, {});

  // Get zone stats
  const zoneStats = zones.filter(z => z !== 'all').map(zone => {
    const zoneTables = tables.filter(t => t.zone === zone);
    const occupied = zoneTables.filter(t => t.status === 'occupied' || t.status === 'billing').length;
    return { zone, total: zoneTables.length, occupied };
  });

  return (
    <div data-testid="table-grid" className="flex flex-col h-full bg-white rounded-lg shadow-sm overflow-hidden">
      {/* Zone Tabs */}
      <div className="flex items-center gap-2 p-3 border-b bg-gray-50 overflow-x-auto">
        {zones.map(zone => {
          const config = ZONE_CONFIG[zone] || { label: zone, icon: '📍' };
          const stats = zone === 'all' 
            ? { total: tables.length, occupied: tables.filter(t => t.status === 'occupied' || t.status === 'billing').length }
            : zoneStats.find(s => s.zone === zone) || { total: 0, occupied: 0 };
          
          return (
            <button
              key={zone}
              data-testid={`zone-btn-${zone}`}
              onClick={() => onZoneChange(zone)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap',
                selectedZone === zone
                  ? 'bg-brand-orange text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              )}
            >
              <span>{config.icon}</span>
              <span>{config.label}</span>
              <span className={cn(
                'text-xs px-1.5 py-0.5 rounded-full',
                selectedZone === zone 
                  ? 'bg-white/20 text-white' 
                  : 'bg-gray-100 text-gray-500'
              )}>
                {stats.occupied}/{stats.total}
              </span>
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 px-4 py-2 border-b text-xs">
        {Object.entries(STATUS_CONFIG).map(([status, config]) => (
          <div key={status} className="flex items-center gap-1.5">
            <span className={cn('w-2.5 h-2.5 rounded-full', config.dot)} />
            <span className="text-gray-600">{config.label}</span>
          </div>
        ))}
      </div>

      {/* Table Grid */}
      <ScrollArea className="flex-1">
        <div className="p-4">
          {selectedZone === 'all' ? (
            // Show grouped by zone
            <div className="space-y-6">
              {Object.entries(tablesByZone).map(([zone, zoneTables]) => (
                <div key={zone}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg">{ZONE_CONFIG[zone]?.icon || '📍'}</span>
                    <h3 className="text-sm font-semibold text-gray-700">
                      {ZONE_CONFIG[zone]?.label || zone}
                    </h3>
                    <span className="text-xs text-gray-400">
                      ({zoneTables.filter(t => t.status === 'occupied' || t.status === 'billing').length}/{zoneTables.length})
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                    {zoneTables.map(table => (
                      <TableCard
                        key={table.id}
                        table={table}
                        isSelected={selectedTable?.id === table.id}
                        onSelect={() => onTableSelect(table)}
                        formatPrice={formatPrice}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Show flat grid for single zone
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {tables.map(table => (
                <TableCard
                  key={table.id}
                  table={table}
                  isSelected={selectedTable?.id === table.id}
                  onSelect={() => onTableSelect(table)}
                  formatPrice={formatPrice}
                />
              ))}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

// Individual Table Card
function TableCard({ table, isSelected, onSelect, formatPrice }) {
  const statusConfig = STATUS_CONFIG[table.status] || STATUS_CONFIG.available;
  const elapsedTime = formatElapsedTime(table.startTime);

  return (
    <button
      data-testid={`table-card-${table.id}`}
      onClick={onSelect}
      className={cn(
        'relative p-4 rounded-xl border-2 transition-all duration-200 text-left w-full',
        'hover:shadow-lg hover:-translate-y-0.5',
        'focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2',
        statusConfig.bg,
        statusConfig.border,
        statusConfig.hover,
        isSelected && 'ring-2 ring-brand-orange ring-offset-2 shadow-lg'
      )}
    >
      {/* Status Indicator */}
      <div className={cn(
        'absolute top-2 right-2 w-3 h-3 rounded-full',
        statusConfig.dot
      )} />

      {/* Table Name */}
      <div className="font-display text-2xl font-bold text-gray-800 mb-1">
        {table.tableNumber || table.table_number || table.id?.toString()?.slice(0, 8)}
      </div>

      {/* Capacity */}
      <div className="flex items-center gap-1 text-gray-500 text-sm mb-2">
        <Users className="w-3.5 h-3.5" />
        <span>
          {table.status === 'occupied' || table.status === 'billing' 
            ? `${table.guests}/${table.seats}` 
            : table.seats}
        </span>
      </div>

      {/* Order Info */}
      {(table.status === 'occupied' || table.status === 'billing') && (
        <div className="mt-auto pt-2 border-t border-gray-200">
          <div className="font-mono font-bold text-gray-800">
            {formatPrice(table.amount || 0)}
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
            <Clock className="w-3 h-3" />
            <span>{elapsedTime}</span>
          </div>
        </div>
      )}

      {/* Reservation Info */}
      {table.status === 'reserved' && table.reservationTime && (
        <div className="mt-auto pt-2 border-t border-gray-200">
          <div className={cn('font-semibold text-sm', statusConfig.text)}>
            Reserved: {table.reservationTime}
          </div>
        </div>
      )}

      {/* Billing indicator */}
      {table.status === 'billing' && (
        <div className="absolute bottom-2 right-2">
          <AlertCircle className="w-4 h-4 text-red-500 animate-pulse" />
        </div>
      )}
    </button>
  );
}
